import React, { useState } from 'react';
import { MessageCircle, FileText, X, Phone, Send, MessageSquare } from 'lucide-react';
import { submitEnquiry } from '../api';
import { useSettings } from '../store';
import useAntiSpam from '../hooks/useAntiSpam';

const FloatingActions = () => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: '', phone: '', message: '' });
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const { fields: antiSpamFields, honeypotInput, readHoneypot } = useAntiSpam();
  const settings = useSettings();
  const wa = (settings.whatsapp || '919650336344').replace(/\D/g, '');
  const phoneStr = settings.phone || '+91-9650336344';
  const phoneTel = phoneStr.startsWith('+') ? phoneStr : `+${phoneStr}`;

  const openChat = () => {
    window.dispatchEvent(new CustomEvent('travelsera:open-chat'));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) return;
    setErr('');
    setLoading(true);
    try {
      await submitEnquiry({
        ...form,
        source: 'floating',
        ...antiSpamFields,
        website: readHoneypot(),
      });
      setSent(true);
      setTimeout(() => {
        setSent(false);
        setOpen(false);
        setForm({ name: '', phone: '', message: '' });
      }, 2000);
    } catch (e2) {
      setErr('Could not send. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Desktop / Tablet: floating right-side stack */}
      <div className="hidden md:flex fixed right-3 top-1/2 -translate-y-1/2 z-40 flex-col gap-2" data-testid="floating-actions-desktop">
        <a
          href={`https://wa.me/${wa}`}
          target="_blank"
          rel="noreferrer"
          className="w-11 h-11 rounded-full bg-[#25D366] text-white flex items-center justify-center shadow-lg hover:scale-110 transition"
          title="WhatsApp"
          data-testid="floating-whatsapp-btn"
        >
          <MessageCircle size={20} />
        </a>
        <button
          onClick={openChat}
          className="w-11 h-11 rounded-full bg-[#7c3aed] text-white flex items-center justify-center shadow-lg hover:scale-110 transition"
          title="Chat with Sera"
          data-testid="floating-chat-btn"
        >
          <MessageSquare size={18} />
        </button>
        <button
          onClick={() => setOpen(true)}
          className="w-11 h-11 rounded-full bg-[#0d64a8] text-white flex items-center justify-center shadow-lg hover:scale-110 transition"
          title="Send Enquiry"
          data-testid="floating-enquiry-btn"
        >
          <FileText size={18} />
        </button>
        <a
          href={`tel:${phoneTel}`}
          className="w-11 h-11 rounded-full bg-[#ffcc29] text-[#0a2a4a] flex items-center justify-center shadow-lg hover:scale-110 transition"
          title="Call"
          data-testid="floating-call-btn"
        >
          <Phone size={18} />
        </a>
      </div>

      {/* Mobile: fixed bottom bar */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-gray-200 shadow-lg flex items-stretch" data-testid="floating-actions-mobile">
        <a
          href={`tel:${phoneTel}`}
          className="flex-1 flex flex-col items-center justify-center py-2 gap-0.5 text-[#0d64a8] active:bg-gray-50"
          data-testid="floating-call-mobile-btn"
        >
          <Phone size={18} />
          <span className="text-[10px] font-medium">Call</span>
        </a>
        <div className="w-px bg-gray-200" />
        <a
          href={`https://wa.me/${wa}`}
          target="_blank"
          rel="noreferrer"
          className="flex-1 flex flex-col items-center justify-center py-2 gap-0.5 text-[#25D366] active:bg-gray-50"
          data-testid="floating-whatsapp-mobile-btn"
        >
          <MessageCircle size={18} />
          <span className="text-[10px] font-medium">WhatsApp</span>
        </a>
        <div className="w-px bg-gray-200" />
        <button
          onClick={openChat}
          className="flex-1 flex flex-col items-center justify-center py-2 gap-0.5 text-[#7c3aed] active:bg-gray-50"
          data-testid="floating-chat-mobile-btn"
        >
          <MessageSquare size={18} />
          <span className="text-[10px] font-medium">Chat</span>
        </button>
        <div className="w-px bg-gray-200" />
        <button
          onClick={() => setOpen(true)}
          className="flex-1 flex flex-col items-center justify-center py-2 gap-0.5 text-[#0a2a4a] active:bg-gray-50"
          data-testid="floating-enquiry-mobile-btn"
        >
          <FileText size={18} />
          <span className="text-[10px] font-medium">Enquiry</span>
        </button>
      </div>

      {open && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-xl w-full max-w-md p-6 relative" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setOpen(false)} className="absolute top-3 right-3 text-gray-400 hover:text-gray-700" data-testid="enquiry-modal-close-btn">
              <X size={18} />
            </button>
            <h3 className="text-xl font-bold text-gray-900 mb-1">Send Enquiry</h3>
            <p className="text-sm text-gray-500 mb-5">We&apos;ll get back to you within 24 hours.</p>
            {sent ? (
              <div className="py-8 text-center">
                <div className="w-14 h-14 rounded-full bg-green-100 text-green-600 flex items-center justify-center mx-auto mb-3">
                  <Send size={22} />
                </div>
                <p className="text-gray-800 font-medium">Enquiry Sent!</p>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-3">
                {honeypotInput}
                <input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} required placeholder="Your Name" className="w-full border border-gray-200 rounded-md px-3 py-2.5 text-sm outline-none focus:border-[#0d64a8]" data-testid="enquiry-name-input" />
                <input value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} required placeholder="Phone Number" className="w-full border border-gray-200 rounded-md px-3 py-2.5 text-sm outline-none focus:border-[#0d64a8]" data-testid="enquiry-phone-input" />
                <textarea value={form.message} onChange={(e) => setForm({...form, message: e.target.value})} rows={3} placeholder="Your travel plans / questions" className="w-full border border-gray-200 rounded-md px-3 py-2.5 text-sm outline-none focus:border-[#0d64a8] resize-none" data-testid="enquiry-message-input" />
                {err && <div className="text-xs text-red-600">{err}</div>}
                <button type="submit" disabled={loading} className="w-full bg-[#0d64a8] hover:bg-[#0a548e] disabled:opacity-70 text-white font-medium py-2.5 rounded-md transition flex items-center justify-center gap-2" data-testid="enquiry-submit-btn">
                  <Send size={14} /> {loading ? 'Sending...' : 'Submit Enquiry'}
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default FloatingActions;
